import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  AccessTime,
  Send,
  WhatsApp,
  Facebook,
  Instagram,
  Twitter,
  ExpandMore,
  Business,
  Schedule,
  Message,
  LinkedIn,
  YouTube
} from '@mui/icons-material';
import { ContactFormData } from '../types';
import { contactPageService } from '../services/firebase';
import WhatsAppButton from '../components/Common/WhatsAppButton';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../utils/getTranslatedData';

const ContactPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [contactData, setContactData] = useState<any>(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchContactData();
  }, []);

  const fetchContactData = async () => {
    try {
      setDataLoading(true);
      setDataError(null);
      let data = await contactPageService.getContactPageData();
      
      // Eğer veri yoksa varsayılan veriyi oluştur
      if (!data) {
        await contactPageService.initializeContactPageData();
        data = await contactPageService.getContactPageData();
      }
      
      setContactData(data);
    } catch (err) {
      console.error('İletişim verileri yüklenirken hata:', err);
      setDataError('Sayfa verileri yüklenirken bir hata oluştu.');
    } finally {
      setDataLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setError(t('contact_us.error_message'));
      return;
    }

    if (!formData.email.includes('@')) {
      setError(t('Lütfen geçerli bir e-posta adresi girin'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Submit form to Firebase
      await contactPageService.submitContactForm(formData);
      
      setSuccess(t('contact_us.success_message'));
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (err) {
      console.error('Error sending message:', err);
      setError(t('contact_us.error_message'));
    } finally {
      setLoading(false);
    }
  };

  const { heroSection, contactInfo, socialMedia, faq, sections } = contactData || {};

  const contactInfoItems = contactInfo && sections?.contactInfo?.isActive ? [
    {
      icon: <Phone />,
      title: t('contact_us.phone_title', 'Telefon'),
      lines: contactInfo.phones
    },
    {
      icon: <Email />,
      title: t('contact_us.email_title', 'E-posta'),
      lines: contactInfo.emails
    },
    {
      icon: <LocationOn />,
      title: t('contact_us.address_title', 'Adres'),
      lines: [
        `${getTranslatedData(contactInfo.address.street, i18n.language)}, ${getTranslatedData(contactInfo.address.district, i18n.language)}`,
        `${getTranslatedData(contactInfo.address.city, i18n.language)}, ${getTranslatedData(contactInfo.address.country, i18n.language)}`
      ]
    },
    {
      icon: <AccessTime />,
      title: t('contact_us.working_hours_title', 'Çalışma Saatleri'),
      lines: [
        `${t('contact_us.monday_friday', 'Pazartesi-Cuma')}: ${getTranslatedData(contactInfo.officeHours.weekdays, i18n.language)}`,
        `${t('contact_us.saturday', 'Cumartesi')}: ${getTranslatedData(contactInfo.officeHours.saturday, i18n.language)}`,
        `${getTranslatedData(contactInfo.officeHours.sunday, i18n.language)}`
      ]
    }
  ] : [];

  const socialLinks = socialMedia && sections?.socialMedia?.isActive ? [
    { icon: <WhatsApp />, name: 'WhatsApp', url: socialMedia.whatsapp || '', color: '#25D366' },
    { icon: <Facebook />, name: 'Facebook', url: socialMedia.facebook || '', color: '#1877F2' },
    { icon: <Instagram />, name: 'Instagram', url: socialMedia.instagram || '', color: '#E4405F' },
    { icon: <Twitter />, name: 'Twitter', url: socialMedia.twitter || '', color: '#1DA1F2' },
    { icon: <LinkedIn />, name: 'LinkedIn', url: socialMedia.linkedin || '', color: '#0077B5' },
    { icon: <YouTube />, name: 'YouTube', url: socialMedia.youtube || '', color: '#FF0000' }
  ].filter(link => link.url && link.url !== '') : [];


  if (dataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (dataError || !contactData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          {dataError || 'Sayfa verileri yüklenemedi'}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{getTranslatedData(heroSection?.title, i18n.language) || t('contact_us.title')} - HatTravel</title>
        <meta name="description" content={getTranslatedData(heroSection?.description, i18n.language) || t('contact_us.description')} />
      </Helmet>
      
      <Box sx={{ 
        backgroundColor: 'var(--light-gray-white)',
        minHeight: '100vh'
      }}>
        {/* Hero Section */}
        <Box sx={{ 
          backgroundColor: 'var(--light-gray-white)',
          py: 8,
          mb: 6
        }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h1" 
                component="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '3rem', md: '4rem' },
                  color: 'var(--navy-blue)',
                  mb: 3
                }}
              >
                {getTranslatedData(heroSection?.title, i18n.language) || t('contact_us.title')}
              </Typography>
              <Typography 
                variant="h5" 
                color="text.secondary"
                sx={{ 
                  maxWidth: 600, 
                  mx: 'auto',
                  fontWeight: 300,
                  lineHeight: 1.6
                }}
              >
                {getTranslatedData(heroSection?.description, i18n.language) || t('contact_us.description')}
              </Typography>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg">
          {/* Error/Success Messages */}
          {error && (
            <Alert severity="error" sx={{ mb: 4, borderRadius: 3 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 4, borderRadius: 3 }}>
              {success}
            </Alert>
          )}

          {/* Contact Info Cards */}
          {(contactInfoItems.length > 0) && (
          <Box sx={{ mb: 8 }}>
            
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 4 }}>
              {contactInfoItems.map((info: any, index: number) => (
                <Box key={index}>
                  <Card sx={{ 
                    height: '100%',
                    backgroundColor: 'white',
                    borderRadius: 4,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    }
                  }}>
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        mb: 3 
                      }}>
                        <Box sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          backgroundColor: 'var(--light-gray)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--light-blue-cta)',
                          fontSize: '2.5rem',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                        }}>
                          {info.icon}
                        </Box>
                      </Box>
                      
                      <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, color: 'var(--navy-blue)', minHeight: 64 }}>
                        {info.title}
                      </Typography>
                      {info.lines.map((line: string, i: number) => (
                        <Typography key={i} variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                          {getTranslatedData(line, i18n.language)}
                        </Typography>
                      ))}
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>
          )}

          {/* Form and Map Section */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, 
            gap: 6, 
            mb: 8,
            alignItems: 'stretch'
          }}>
            {/* Contact Form */}
            {(sections?.contactForm?.isActive ?? true) && (
            <Card sx={{ 
              p: 4, 
              backgroundColor: 'white', 
              borderRadius: 4,
              boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <CardContent>
                <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: 'var(--navy-blue)', mb: 4 }}>
                  {getTranslatedData(contactData?.contactForm?.title, i18n.language) || 'Bize Mesaj Gönderin'}
                </Typography>
                
                <form onSubmit={handleSubmit} noValidate>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 3
                  }}>
                      <TextField
                        fullWidth
                        label={t('contact_us.form.name', 'Adınız Soyadınız')}
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                      />
                    
                      <TextField
                        fullWidth
                        label={t('contact_us.form.email', 'E-posta Adresiniz')}
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                      />
                    
                      <TextField
                        fullWidth
                        label={t('contact_us.form.phone', 'Telefon Numaranız (İsteğe Bağlı)')}
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        variant="outlined"
                      />
                    
                      <TextField
                        fullWidth
                        label={t('contact_us.form.subject', 'Konu')}
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        variant="outlined"
                      />
                    
                      <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}>
                        <TextField
                          fullWidth
                          label={t('contact_us.form.message', 'Mesajınız')}
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          multiline
                          rows={6}
                          required
                          variant="outlined"
                        />
                      </Box>
                    
                      <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}>
                        <Button
                          type="submit"
                          variant="contained"
                          fullWidth
                          disabled={loading}
                          startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <Send />}
                          sx={{
                            py: 2,
                            backgroundColor: 'var(--light-blue-cta)',
                            '&:hover': {
                              backgroundColor: 'var(--cobalt-blue)',
                            },
                            fontWeight: 600,
                            fontSize: '1.1rem'
                          }}
                        >
                          {loading ? t('contact_us.form.sending', 'Gönderiliyor...') : t('contact_us.form.send', 'Mesajı Gönder')}
                        </Button>
                      </Box>
                  </Box>
                </form>
              </CardContent>
            </Card>
            )}
            
            {/* Map */}
            {sections?.map?.isActive && contactInfo?.mapLocation && (
              <Box sx={{ position: 'relative', borderRadius: 4, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.05)', minHeight: { xs: 300, lg: 'auto' } }}>
                <iframe
                  src={`https://www.google.com/maps/embed/v1/place?key=${contactInfo.mapLocation.googleMapsApiKey || 'YOUR_API_KEY'}&q=${contactInfo.mapLocation.latitude},${contactInfo.mapLocation.longitude}`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen={true}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
                <Button
                  variant="contained"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${contactInfo.mapLocation.latitude},${contactInfo.mapLocation.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<LocationOn />}
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: 16,
                    backgroundColor: 'var(--navy-blue)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'var(--light-blue-cta)',
                    }
                  }}
                >
                  Yol Tarifi Al
                </Button>
              </Box>
            )}
          </Box>
          
          {/* Social Media Section */}
          {socialLinks.length > 0 && (
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h2" 
              gutterBottom 
              sx={{ 
                fontWeight: 700, 
                color: 'var(--navy-blue)',
                mb: 4
              }}
            >
              Bizi Takip Edin
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
              {socialLinks.map((link: any) => (
                <Button
                  key={link.name}
                  variant="contained"
                  href={link.url}
                  target="_blank"
                  startIcon={link.icon}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    backgroundColor: link.color,
                    color: 'white',
                    py: 1.5,
                    px: 3,
                    '&:hover': {
                      backgroundColor: link.color,
                      opacity: 0.9,
                      transform: 'scale(1.05)'
                    }
                  }}
                >
                  {link.name}
                </Button>
              ))}
            </Box>
          </Box>
          )}
          
          {/* FAQ Section */}
          {sections?.faq?.isActive && faq?.items?.length > 0 && (
          <Box sx={{ mb: 8 }}>
            <Typography 
              variant="h2" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                textAlign: 'center', 
                mb: 6,
                color: 'var(--navy-blue)'
              }}
            >
              Sıkça Sorulan Sorular
            </Typography>
            
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
              {faq.items
                ?.filter((item: any) => item.isActive)
                ?.sort((a: any, b: any) => a.order - b.order)
                ?.map((item: any) => (
                  <Accordion key={item.id} sx={{
                    backgroundColor: 'white',
                    boxShadow: 'none',
                    border: '1px solid var(--gray-shadow)',
                    borderRadius: 3,
                    mb: 2,
                    '&:before': {
                      display: 'none',
                    },
                    '&.Mui-expanded': {
                      margin: '0 0 16px 0',
                    }
                  }}>
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: 'var(--light-blue-cta)' }} />}
                      aria-controls={`${item.id}-content`}
                      id={`${item.id}-header`}
                      sx={{
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          gap: 2
                        }
                      }}
                    >
                      <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 'var(--light-gray)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--light-blue-cta)',
                        flexShrink: 0
                      }}>
                        ?
                      </Box>
                      <Typography sx={{ fontWeight: 600, color: 'var(--navy-blue)' }}>
                        {getTranslatedData(item.question, i18n.language)}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{
                      borderTop: '1px solid var(--gray-shadow)',
                      p: 3
                    }}>
                      <Typography sx={{ color: 'var(--navy-blue)', opacity: 0.8 }}>
                        {getTranslatedData(item.answer, i18n.language)}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
              ))}
            </Box>
          </Box>
          )}
        </Container>
      </Box>

      <WhatsAppButton />
    </>
  );
};

export default ContactPage; 